import React from 'react';
import {
  Box, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Typography,
} from '@mui/material';
import { Catalogue, MeasureRef } from '../../../services/indicatorRegistryApi';

interface Props {
  label: string;
  value: MeasureRef;
  catalogue: Catalogue;
  disabled?: boolean;
  onChange: (value: MeasureRef) => void;
}

/** One side of a formula: a catalogue measure, optionally filtered. */
const MeasurePicker: React.FC<Props> = ({ label, value, catalogue, disabled, onChange }) => {
  const measure = catalogue.measures.find(m => m.code === value.measure);

  const toggle = (dim: string, code: string) => {
    const current = value.filters[dim] || [];
    const next = current.includes(code) ? current.filter(c => c !== code) : [...current, code];
    const filters = { ...value.filters };
    if (next.length) filters[dim] = next;
    else delete filters[dim];
    onChange({ ...value, filters });
  };

  return (
    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
        <InputLabel>Measure</InputLabel>
        <Select
          label="Measure"
          value={value.measure}
          disabled={disabled}
          onChange={e => onChange({ measure: e.target.value, filters: {} })}
        >
          {catalogue.measures.map(m => (
            <MenuItem key={m.code} value={m.code}>
              {m.label}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {m.source}{m.by_year ? '' : ' · current snapshot'}{m.area_only ? ' · district level up' : ''}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {measure && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {measure.description}
          </Typography>
          {measure.by_year && (
            <FormControl size="small" sx={{ mt: 1.5, minWidth: 220 }}>
              <InputLabel>Year</InputLabel>
              <Select
                label="Year"
                value={value.year_offset || 0}
                disabled={disabled}
                onChange={e => {
                  const offset = Number(e.target.value);
                  const { year_offset, ...rest } = value;
                  onChange(offset ? { ...rest, year_offset: offset } : rest);
                }}
              >
                <MenuItem value={0}>The year computed</MenuItem>
                <MenuItem value={-1}>The year before</MenuItem>
                <MenuItem value={1}>The year after</MenuItem>
              </Select>
            </FormControl>
          )}
          {measure.dimensions.length > 0 && (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Only count (leave all off to count everything)
              </Typography>
              {measure.dimensions.map(({ code }) => {
                const dim = catalogue.dimensions.find(d => d.code === code)!;
                const selected = value.filters[code] || [];
                return (
                  <Box key={code} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ width: 110, color: 'text.secondary' }}>{dim.label}</Typography>
                    {dim.values.map(v => (
                      <Chip
                        key={v.code}
                        label={v.label}
                        size="small"
                        disabled={disabled}
                        color={selected.includes(v.code) ? 'primary' : 'default'}
                        variant={selected.includes(v.code) ? 'filled' : 'outlined'}
                        onClick={() => toggle(code, v.code)}
                      />
                    ))}
                  </Box>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default MeasurePicker;
